<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal :visible="show" :data="model" :title="$t('Identification Details')"
                   @show="onShow" @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)"
                   size="xl"
                   :show-confirmation="showConfirmation" :ok-title="$t('Submit')" :is-loading="loading" scrollable>
      <div class="row">
        <div class="col-md-6">
          <div class="row">
            <div class="col-md-6">
              <input-widget :model="model"
                            attribute="document_type"
                            value-field="value"
                            display-field="${item.key}"
                            type="select2"
                            :items="customerIdentificationTypes"/>
            </div>
             <div class="col-md-6">
              <input-widget :model="model" attribute="personal_identification_number"/>
            </div>
            <div class="col-md-6">
              <input-widget :model="model" attribute="document_number"/>
            </div>
            <div class="col-md-6">
              <input-widget :model="model" attribute="document_issue_date" type="date"/>
            </div>
            <div class="col-md-6">
              <input-widget :model="model" attribute="place_of_issue"/>
            </div>
            <div class="col-md-6">
              <input-widget :model="model" attribute="document_expiry_date" type="date"/>
            </div>
            <div class="col-md-6">
              <input-widget :model="model" attribute="date_of_birth" type="date"/>
            </div>
            <div class="col-md-6">
              <country-select-input-widget :model="model" attribute="citizenship_code"/>
            </div>
            <div class="col-md-6">
              <country-select-input-widget :model="model" attribute="residency_code"/>
            </div>
            <div class="col-md-6">
              <country-select-input-widget :model="model" attribute="country_of_birth_code"/>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <hr>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <input-widget :model="model" attribute="company_reg_number"/>
            </div>
             <div class="col-md-6" v-for="field in identificationDynamicFields">
              <input-widget :key="`${field.key}`"
                            :model="model" :rules="getRulesByKey(field.key)"
                            :attribute="`${field.key}`"
                            :type="field.type"/>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="row">
            <div class="col-md-12">
              <h5>{{ model.attributeLabels.government_issued_id }}</h5>
              <file-uploader :show-preview="false"
                             @onItemsAdd="onItemsAdd('government_issued_id')"
                             @onItemRemove="({index, uuid}) => onItemRemove('government_issued_id', index, uuid)"
                             :items-prop="government_issued_id.files"
                             :hint-text="false"
                             :uploading-progress="government_issued_id.uploadingProgress"
                             :uploading-progress-value="government_issued_id.uploadingProgressValue"
                             :accept="fileUploaderAccept"
                             ref="governmentIssuedIdFileUploader" class="mb-4"/>
            </div>
            <div class="col-md-12">
              <h5>{{ model.attributeLabels.proof_of_address }}</h5>
              <file-uploader :show-preview="false"
                             @onItemsAdd="onItemsAdd('proof_of_address')"
                             @onItemRemove="({index, uuid}) => onItemRemove('proof_of_address', index, uuid)"
                             :items-prop="proof_of_address.files"
                             :hint-text="false"
                             :uploading-progress="proof_of_address.uploadingProgress"
                             :uploading-progress-value="proof_of_address.uploadingProgressValue"
                             :accept="fileUploaderAccept"
                             ref="proofOfAddressFileUploader" class="mb-4"/>
            </div>
            <div class="col-md-12">
              <h5>{{ model.attributeLabels.employment_information }}</h5>
              <file-uploader :show-preview="false"
                             @onItemsAdd="onItemsAdd('employment_information')"
                             @onItemRemove="({index, uuid}) => onItemRemove('employment_information', index, uuid)"
                             :items-prop="employment_information.files"
                             :hint-text="false"
                             :uploading-progress="employment_information.uploadingProgress"
                             :uploading-progress-value="employment_information.uploadingProgressValue"
                             :accept="fileUploaderAccept"
                             ref="employmentInformationFileUploader" class="mb-4"/>
            </div>
            <div class="col-md-12">
              <h5>{{ model.attributeLabels.company_id_documents }}</h5>
              <file-uploader :show-preview="false"
                             @onItemsAdd="onItemsAdd('company_id_documents')"
                             @onItemRemove="({index, uuid}) => onItemRemove('company_id_documents', index, uuid)"
                             :items-prop="company_id_documents.files"
                             :hint-text="false"
                             :uploading-progress="company_id_documents.uploadingProgress"
                             :uploading-progress-value="company_id_documents.uploadingProgressValue"
                             :accept="fileUploaderAccept"
                             ref="company_id_documentsFileUploader" class="mb-4"/>
            </div>
          </div>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import {createNamespacedHelpers} from 'vuex';
  import ArtisioModal from "../../../../../core/components/artisio-modal/ArtisioModal";
  import InputWidget from "../../../../../core/components/input-widget/InputWidget";
  import CustomerIdentificationFormModel from "@/modules/customer/models/CustomerIdentificationForm.model";
  import CountrySelectInputWidget from "@/core/widgets/CountrySelectInputWidget";
  import FileUploader from "../../../../../core/components/file-uploader/FileUploader.vue";
  import bucketHelperService from "@/core/services/bucketHelper.service";

  const {mapState, mapActions} = createNamespacedHelpers('customer/customerView');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "CustomerIdentificationModal",
    components: {FileUploader, CountrySelectInputWidget, InputWidget, ArtisioModal},
    data: () => {
      return {
        loading: false,
        model: new CustomerIdentificationFormModel({}),
        showConfirmation: false,
        government_issued_id: {
          files: [],
          uploadingProgressValue: 0,
          uploadingProgress: null
        },
        proof_of_address: {
          files: [],
          uploadingProgressValue: 0,
          uploadingProgress: null
        },
        employment_information: {
          files: [],
          uploadingProgressValue: 0,
          uploadingProgress: null
        },
        company_id_documents: {
          files: [],
          uploadingProgressValue: 0,
          uploadingProgress: null
        }
      }
    },
    watch: {
      editObject() {
        if (this.editObject) {
          this.model = new CustomerIdentificationFormModel(this.editObject);
          this.government_issued_id.files = this.model.government_issued_id_files;
          this.proof_of_address.files = this.model.proof_of_address_files;
          this.employment_information.files = this.model.employment_information_files;
          this.company_id_documents.files = this.model.company_id_documents_files;
        }
      }
    },
    computed: {
      ...mapState({
        show: state => state.termsData.identificationModal.show,
        editObject: state => state.termsData.identificationModal.object,
      }),
      ...globalMapState({
        customerIdentificationTypes: state => state.customerIdentificationTypes,
        identityVerificationAttachmentTypes: state => state.identityVerificationAttachmentTypes,
        identityVerificationMaxFileSize: state => state.identityVerificationMaxFileSize,
        identityVerificationMaxFileCount: state => state.identityVerificationMaxFileCount,
        identificationDynamicFields: state => state.identificationDynamicFields,
      }),
      fileUploaderAccept() {
        return [{formats: this.identityVerificationAttachmentTypes, maxSize: this.identityVerificationMaxFileSize}]
      }
    },
    methods: {
      ...mapActions(['hideCustomerIdentificationModal', 'saveCustomerIdentification', 'getTermsData', 'getIdentificationData']),
      async onItemsAdd(fileType) {
        const maxCount = this.identityVerificationMaxFileCount;

        if (this[fileType].files.length > maxCount) {
          // this.clearData();
          this[fileType].files = this[fileType].files.slice(0, maxCount)
          this.$errorDialog(
              this.$i18n.t("You can not upload more than {count} files.", {count: maxCount}),
              this.$i18n.t('Attachments')
          );
        }
      },
      async onSubmit() {
        this.model.resetErrors()

        this.loading = true;

        this.model.government_issued_id = this.government_issued_id.files.filter(f => !f.uuid).map(f => ({
          size: f.file.size,
          name: f.file.name
        }))
        this.model.proof_of_address = this.proof_of_address.files.filter(f => !f.uuid).map(f => ({
          size: f.file.size,
          name: f.file.name
        }))
        this.model.employment_information = this.employment_information.files.filter(f => !f.uuid).map(f => ({
          size: f.file.size,
          name: f.file.name
        }))
        this.model.company_id_documents = this.company_id_documents.files.filter(f => !f.uuid).map(f => ({
          size: f.file.size,
          name: f.file.name
        }))
        this.model.deleted_files = [
          ...this.government_issued_id.files.filter(f => f.uuid && f.to_be_deleted).map(f => f.uuid),
          ...this.proof_of_address.files.filter(f => f.uuid && f.to_be_deleted).map(f => f.uuid),
          ...this.employment_information.files.filter(f => f.uuid && f.to_be_deleted).map(f => f.uuid),
          ...this.company_id_documents.files.filter(f => f.uuid && f.to_be_deleted).map(f => f.uuid),
        ]

        let response = await this.saveCustomerIdentification(this.model.toJSON());

        if (response.success) {
          this.showConfirmation = false;
          const files_to_upload = response.body.customer_identification_files.filter(file => !file.attachment.uploaded)
          const files = [
            ...this.government_issued_id.files.filter(f => !f.uuid),
            ...this.proof_of_address.files.filter(f => !f.uuid),
            ...this.employment_information.files.filter(f => !f.uuid),
            ...this.company_id_documents.files.filter(f => !f.uuid)
          ]
          for (let file of files_to_upload) {
            const file_to_upload = files.filter(f => f.file.size === file.attachment.size && f.file.name === file.attachment.name)
            if (!file_to_upload.length) {
              continue
            }
            let body = await file_to_upload[0].blobPromise.then(blob => blob)
            await bucketHelperService.uploadImageToBucket(file.attachment.presigned_url, body)
          }
          this.hideCustomerIdentificationModal();
          this.$toast('success', this.$t('Customer identification details successfully saved.'));
          this.getIdentificationData()
          this.getTermsData()

        } else {
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else {
            this.model.setMultipleErrors(response.body);
          }
        }
        this.loading = false;
      },
      onShow() {
        this.showConfirmation = true;
      },
      onHidden() {
        this.model = new CustomerIdentificationFormModel();
        this.hideCustomerIdentificationModal();
      },
      getRulesByKey(key) {
        if (this.rules && this.rules[key]) {
          return this.rules[key]
        }
        return null
      },
    },
  }
</script>

<style lang="scss" scoped>
</style>

